/**
 * The client-side mirror of the backend's save-time limits — collection ceilings, field ranges and
 * the progression bounds — plus the one predicate that is not a plain comparison.
 *
 * Document-model ADR §10. The point is stated there and is worth repeating because it decides what
 * belongs in this file: **the client validates so that a save rarely fails against a rule the UI
 * already enforced, never as the primary gate.** The backend re-validates everything regardless, so
 * a mirror that drifts costs a confusing 400 rather than a corrupted plan — and a mirror that
 * FABRICATES a limit the backend does not have is worse than none, because it refuses work the
 * product allows.
 *
 * ── Every value below was read out of the backend, not out of the ADR ────────────────────────
 * That distinction produced two corrections, both recorded rather than silently applied:
 *
 * · **RIR is 0–10, not 0–20.** ADR §10 says 20. `Domain/Measurements/MeasurementFieldCatalog.cs`
 *   says 10, with a comment naming the change: "RPE and RIR share 0–10 and zero is legal on both
 *   (RIR 0 means 'to failure'), which lowers RPE's old floor of 1 and RIR's old ceiling of 20". The
 *   ADR's list predates the 31 July measurement-fields migration. A client mirroring 0–20 would
 *   accept an RIR of 15 and hand the trainer a 400 from a field it had just told them was fine.
 * · **`Speed` has a range and the ADR omits it entirely** — 0–100 km/h, step 0.1, added by the same
 *   migration. This app reads and re-emits `prescribedSpeed` (`responseToDraft`), so it is a field
 *   this client can put on the wire and therefore one it owes a bound.
 *
 * `Tempo` carries no numeric range — it is free text at `varchar(16)`, and the length is the whole
 * rule.
 *
 * ── What this module deliberately does NOT do ────────────────────────────────────────────────
 * It does not validate a draft. There is no `validateDraft(draft)` here, and adding one now would be
 * a seam for a caller that does not exist: PR-B1 authors two fields, and the surfaces that author
 * the rest — the set table (PR-B2), the phase editor and the progression bar (PR-D) — each validate
 * the field they own, at the input, where the trainer can see the answer. What they share is this
 * table of numbers, which is the part that must not be written twice.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

/**
 * The collection ceilings — the backend's DoS guards, and the shape rules that fall out of them.
 *
 * Worst case is 12 × 14 × 4 × 20 × 20 across 52 cycles, which is finite and enormous; these are
 * guards, not a description of a plan anyone writes.
 */
export const COLLECTION_CAPS = {
  /** `PostPlanTemplateRequest.Phases`, `[MinLength(1)] [MaxLength(12)]`. */
  phasesPerPlan: { min: 1, max: 12 },
  /** `PostPhaseTemplateInput.Days`. A phase with no day cannot be serialised at all. */
  daysPerPhase: { min: 1, max: 14 },
  /**
   * `PostDayTemplateInput.Sessions`. The backend models a genuine two-a-day; **this builder authors
   * exactly one** and refuses to open a plan carrying more (`responseToDraft`), because sessions
   * have no slot token and a whole-tree save would silently reattribute them.
   */
  sessionsPerDay: { min: 1, max: 4 },
  /** `PostSessionTemplateInput.Exercises`. A session with no exercise is structurally invalid. */
  exercisesPerSession: { min: 1, max: 20 },
  /** `PostExerciseTemplateInput.Sets` — the AUTHORED base, never the materialised grid. */
  setsPerExercise: { min: 1, max: 20 },
  /** `PostPhaseTemplateInput.CycleCount`. Weeks when date-based, cycles when sequence-based. */
  cyclesPerPhase: { min: 1, max: 52 },
  /** `PostExerciseTemplateInput.SetOverrides` — 52 cycles × 20 sets, the whole grid pinned. */
  overridesPerExercise: { min: 0, max: 1040 },
} as const;

/**
 * The plan-wide cycle ceiling (Σ `CycleCount`). **Growth-relative** — see `exceedsTotalCycleCap`,
 * which is the only correct way to compare against it.
 */
export const MAX_TOTAL_CYCLES = 52;

/**
 * The largest absolute cycle number the wire will carry, and it is **not** `MAX_TOTAL_CYCLES`.
 *
 * `PostPlanTemplateRequest.MaxAbsoluteCycle` is `MaxCyclesPerPhase * MaxPhases`, the theoretical
 * ceiling rather than the cap a plan is actually held to, and every window field is ranged against
 * it: `[Range(1, MaxAbsoluteCycle)]` on `ActivationCycle` and `RetirementCycle`, and
 * `ValidateExcludedCycles` refusing any hole outside `1..MaxAbsoluteCycle` with a message naming the
 * offending number. It is derived here rather than written as `624`, so a backend that widens either
 * cap moves this with it.
 *
 * The distinction matters because it is the ONLY bound a window coordinate is refused for. A bound
 * outside its own phase is stored and ignored (`PlanTree`); a bound outside this is a 400.
 */
export const MAX_ABSOLUTE_CYCLE = COLLECTION_CAPS.cyclesPerPhase.max * COLLECTION_CAPS.phasesPerPlan.max;

/** A cycle coordinate held to the range the wire accepts. Never null — an absent bound WIDENS. */
export function clampToPlanCycles(cycle: number): number {
  return cycle < 1 ? 1 : cycle > MAX_ABSOLUTE_CYCLE ? MAX_ABSOLUTE_CYCLE : cycle;
}

/**
 * Would this submission be refused with `plan_total_cycles_capped`?
 *
 * The rule is NOT "the total is over 52". `Services/PhaseGrowthPolicy.cs` refuses only when the
 * submission is over the cap **and longer than what the plan already spans**:
 *
 *     submittedTotal > MaxTotalCycles && submittedTotal > currentTotalCycles
 *
 * A legacy plan already past the cap therefore stays editable and shortenable, which is the whole
 * reason the rule is shaped this way. A client mirroring the naive comparison would lock such a plan
 * out of every edit — including the edit that would bring it back under the cap.
 *
 * `currentTotalCycles` is the span the plan had when it was LOADED, not the draft's current one.
 */
export function exceedsTotalCycleCap(submittedTotal: number, currentTotalCycles: number): boolean {
  return submittedTotal > MAX_TOTAL_CYCLES && submittedTotal > currentTotalCycles;
}

/** An inclusive numeric range, as the backend states it. */
export interface CapRange {
  readonly min: number;
  readonly max: number;
}

/** Is the value inside the range? `null` is always allowed — every one of these fields is optional. */
export function withinRange(value: number | null | undefined, range: CapRange): boolean {
  if (value == null) return true;
  return value >= range.min && value <= range.max;
}

/** Text length limits. A `min` of 1 means the wire rejects an empty string, not merely a null. */
export const TEXT_LIMITS = {
  planName: { min: 1, max: 200 },
  planDescription: { min: 0, max: 2000 },
  coverImageKey: { min: 0, max: 64 },
  phaseName: { min: 1, max: 120 },
  dayName: { min: 0, max: 80 },
  slotToken: { min: 1, max: 64 },
  sessionName: { min: 1, max: 120 },
  sessionNotes: { min: 0, max: 2000 },
  exerciseGroup: { min: 0, max: 16 },
  exerciseNotes: { min: 0, max: 2000 },
} as const;

/**
 * `PrescribedTempo`'s ceiling, on its own because it is the one length this app can newly breach.
 * `SetMeasurementValues` declares `[MaxLength(16)]` and the column is `varchar(16)`. Mobile never
 * read the field, so no client has ever sent one; this app does (`responseToDraft`), which makes the
 * editor that authors a tempo the first thing that has to enforce it — `draftToRequest` never
 * truncates, because silently shortening a trainer's notation is data loss dressed as safety.
 */
export const TEMPO_MAX_LENGTH = 16;

/**
 * Per-set numeric ranges, from `Domain/Measurements/MeasurementFieldCatalog.cs` — the backend's own
 * dictionary, which is also what decides a field's step and its default visibility. `step` is
 * carried because an input that offers a spinner has to move by the same increment the backend
 * accepts; nothing in PR-B1 reads it, and the set table does.
 */
export const SET_FIELD_RANGES = {
  weight: { min: 0, max: 2000, step: 0.5 },
  reps: { min: 0, max: 10000, step: 1 },
  duration: { min: 0, max: 86400, step: 1 },
  distance: { min: 0, max: 100000, step: 1 },
  rounds: { min: 0, max: 10000, step: 1 },
  speed: { min: 0, max: 100, step: 0.1 },
  rpe: { min: 0, max: 10, step: 0.5 },
  rir: { min: 0, max: 10, step: 1 },
} as const;

/**
 * `PostSetInput.PrescribedRestTime` and `PrescribedPercent`, which carry their bounds on the DTO
 * itself rather than in the field catalogue — rest is not a measurement field, and the percent is a
 * 0–1 FRACTION on the wire even though every surface shows it as a percentage.
 */
export const REST_SECONDS_RANGE = { min: 0, max: 86400 } as const;
export const PERCENT_RANGE = { min: 0, max: 1 } as const;

/**
 * Is this percent authorable **on a ladder member** — a back-off set, or an AMRAP set carrying a
 * percent? Strictly positive, at most 1.
 *
 * The bound is OPEN AT THE BOTTOM and `PERCENT_RANGE` is not, which looks like an inconsistency and
 * is the contract. Two layers disagree on purpose:
 *
 * · `PostSetInput.PrescribedPercent` and `PostSetOverrideInput.PrescribedPercent` both carry
 *   `[Range(0.0, 1.0)]`, so model validation accepts `0` — that is `PERCENT_RANGE`, and it is the
 *   right bound for a plain %1RM column on a Working or Top set.
 * · `Domain/Progression/SetShapeValidator.RequireLadderPercentInRange` rejects `<= 0` for a ladder
 *   member, with its own reason: "the percent is a fraction of the top … and 0 is not a load, so
 *   the range is open at the bottom". A back-off at 0% of the top set prescribes nothing.
 *
 * So a `0` passes the DTO attribute and then fails deeper, as a 400 the trainer reads after saving.
 * Stated as a predicate rather than left to `withinRange`, which is inclusive at both ends — exactly
 * the shape, and exactly the reason, that `isAuthorableMultiplier` exists for the RealMax
 * multiplier. Both mirror a backend `is <= 0m` test.
 */
export function isAuthorableLadderPercent(percent: number): boolean {
  return percent > PERCENT_RANGE.min && percent <= PERCENT_RANGE.max;
}

/** Day scheduling coordinates. A date-based day needs the first; a sequence-based day the second. */
export const DAY_OF_WEEK_RANGE = { min: 1, max: 7 } as const;
export const REST_DAYS_AFTER_RANGE = { min: 0, max: 30 } as const;
/** `PostSessionTemplateInput.EstimatedDurationMinutes` — the ONLY duration this app knows. */
export const DURATION_MINUTES_RANGE = { min: 1, max: 600 } as const;

/**
 * Progression-rule bounds (document-model ADR §7), each read from the module that enforces it.
 *
 * The `Percent` delta's ceiling is tighter than a single application would need, and deliberately
 * so: a Static rule COMPOUNDS `(1 + delta/100)^cycleOffset` across a phase at materialisation, so
 * 100% per cycle is the proven-safe ceiling on the tightest path, and the owner's decision on
 * 2026-07-24 was one uniform number rather than a per-module allowance nobody could program with.
 */
export const PROGRESSION_BOUNDS = {
  /** `Services/Progression/WeightDeltaBounds.cs`. */
  weightDeltaKg: { min: -1000, max: 1000 },
  weightDeltaPercent: { min: -100, max: 100 },
  /** `Services/Progression/StaticProgressionModule.cs` — signed points on the effort axis. */
  effortDeltaPoints: { min: -10, max: 10 },
  /** `PreviousExecutionProgressionModule` / `RealMaxProgressionModule`, both `±50`. */
  offsetKg: { min: -50, max: 50 },
  /** `RealMaxProgressionModule` — strictly greater than zero, so `min` is NOT a legal value. */
  multiplier: { min: 0, max: 10 },
} as const;

/** The step an effort delta must land on. RPE moves in halves; RIR is whole reps. */
export const EFFORT_DELTA_STEP = { Rpe: 0.5, Rir: 1 } as const;

/**
 * Is this effort delta authorable on this axis? Both halves matter and they are enforced in
 * different places on the backend — the magnitude by `MaxEffortPointsDelta`, the grid by two
 * separate checks — so a client testing only the magnitude passes an RPE delta of 0.3 straight into
 * a 400.
 */
export function isAuthorableEffortDelta(delta: number, metric: 'Rpe' | 'Rir'): boolean {
  if (!withinRange(delta, PROGRESSION_BOUNDS.effortDeltaPoints)) return false;
  const step = EFFORT_DELTA_STEP[metric];
  // Scaled through `Math.round` rather than tested with `%`.
  //
  // The justification written here until 2026-08-02 was arithmetically FALSE and is corrected rather
  // than deleted, because it is the kind of claim a reader would otherwise copy into the next
  // predicate: it said `0.3 % 0.5` is 0.29999999999999993 and `1.5 % 0.5` is 0.4999999999999999.
  // Measured in Node, `0.3 % 0.5` is exactly `0.3` and `1.5 % 0.5` is exactly `0`. Both steps in
  // `EFFORT_DELTA_STEP` — 0.5 and 1 — are binary-exact, so across the whole ±10 range there is no
  // legal multiple `%` rejects and no illegal value it accepts. On these two axes the naive form
  // would work.
  //
  // The scaled form stays anyway, and the honest reason is the OTHER steps in this file: `%` is
  // exact only while the step is a power of two, and `SET_FIELD_RANGES.speed.step` is 0.1, where
  // `4.2 % 0.1` is 0.09999999999999995 rather than 0. A step-checking predicate that silently
  // depends on its step being binary-exact is one field away from being wrong, and the dependency
  // would be invisible at the call site. (`Math.round` is not a cure either — `0.3 / 0.1 * 0.1` is
  // 0.30000000000000004 — so a 0.1-stepped field needs its own scaling, not this function.)
  return Math.round(delta / step) * step === delta;
}

/**
 * Is this multiplier authorable? Strictly positive, at most ten. Stated as a predicate rather than
 * left to `withinRange`, which is inclusive at both ends and would accept the illegal zero.
 */
export function isAuthorableMultiplier(multiplier: number): boolean {
  return multiplier > PROGRESSION_BOUNDS.multiplier.min && multiplier <= PROGRESSION_BOUNDS.multiplier.max;
}
